import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Badge,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaSave,
  FaTimes,
  FaUsers,
  FaUser
} from 'react-icons/fa';
import LookupLayout from '../../layout/LookupLayout';

const UserGroupForm = ({ show, onHide, onSave, editingGroup }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    user_ids: [],
    is_active: true
  });

  const [selectedUsers, setSelectedUsers] = useState([]); // Store full user objects with id and label
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lookupKey, setLookupKey] = useState(0); // Key to force LookupLayout reset

  // Populate form when editing
  useEffect(() => {
    if (editingGroup) {
      console.log('Editing group:', editingGroup);

      setFormData({
        name: editingGroup.name || '',
        description: editingGroup.description || '',
        user_ids: editingGroup.user_ids || [],
        is_active: editingGroup.is_active !== false
      });

      // Use user details from backend (users field)
      // Backend now sends a 'users' array with user details
      if (editingGroup.users && Array.isArray(editingGroup.users)) {
        const users = editingGroup.users.map(user => ({
          id: user.id,
          label: `${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || user.id
        }));
        setSelectedUsers(users);
      } else {
        // Fallback: if no users field, use user_ids
        const userIds = editingGroup.user_ids || [];
        const users = userIds.map(id => ({ id, label: id }));
        setSelectedUsers(users);
      }
    } else {
      // Reset form for new group
      setFormData({
        name: '',
        description: '',
        user_ids: [],
        is_active: true
      });
      setSelectedUsers([]);
      setLookupKey(0);
    }
  }, [editingGroup, show]);

  // Clear error when modal opens/closes
  useEffect(() => {
    if (!show) {
      setError('');
      setSaving(false);
    }
  }, [show]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleUserSelect = (userId, userLabel) => {
    console.log('User selected:', userId, userLabel);

    // Check for null/empty values
    if (!userId || !userLabel) {
      console.log('Ignoring empty user selection');
      return;
    }

    // Check if user already added
    if (selectedUsers.some(u => u.id === userId)) {
      setError('User already added to the group');
      setTimeout(() => setError(''), 3000);
      // Reset the lookup field to clear the invalid selection
      setLookupKey(prev => prev + 1);
      return;
    }

    // Add user to selected list
    const newUser = { id: userId, label: userLabel };
    const updatedUsers = [...selectedUsers, newUser];
    setSelectedUsers(updatedUsers);

    // Update formData user_ids
    setFormData({
      ...formData,
      user_ids: updatedUsers.map(u => u.id)
    });

    // Reset the lookup field by changing its key
    setLookupKey(prev => prev + 1);
  };

  const handleRemoveUser = (userId) => {
    const updatedUsers = selectedUsers.filter(u => u.id !== userId);
    setSelectedUsers(updatedUsers);

    // Update formData user_ids
    setFormData({
      ...formData,
      user_ids: updatedUsers.map(u => u.id)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || formData.name.trim() === '') {
      setError('Group name is required');
      return;
    }

    if (formData.name.length > 100) {
      setError('Group name must be 100 characters or less');
      return;
    }

    if (formData.description && formData.description.length > 500) {
      setError('Description must be 500 characters or less');
      return;
    }

    setSaving(true);

    try {
      // Prepare data for API
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        user_ids: formData.user_ids,
        is_active: formData.is_active
      };

      console.log('Submitting group data:', dataToSave);

      const result = await onSave(dataToSave, setError);

      if (result?.success) {
        // Form will be closed by parent component
        // Reset handled in useEffect
      }
    } catch (err) {
      console.error('Error in form submit:', err);
      setError('An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaUsers className="me-2" />
          {editingGroup ? 'Edit User Group' : 'Create User Group'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>
                  Group Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter group name"
                  required
                  maxLength={100}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  A unique name for this group (max 100 characters)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter group description (optional)"
                  maxLength={500}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Optional description for this group (max 500 characters)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  label="Active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  Inactive groups are hidden from most views
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          {/* Add Users Section */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Label>
                <FaUser className="me-2" />
                Add Users
              </Form.Label>
              <LookupLayout
                key={lookupKey}
                objectName="user"
                searchFields="firstname,lastname,email"
                displayFields="firstname,lastname"
                value={null}
                defaultLabel=""
                onChange={handleUserSelect}
                placeholder="Search users by name or email..."
                disabled={saving}
              />
              <Form.Text className="text-muted">
                Search and select users to add to this group
              </Form.Text>
            </Col>
          </Row>

          {/* Selected Users List */}
          {selectedUsers.length > 0 && (
            <Row>
              <Col md={12}>
                <Form.Label>
                  <FaUsers className="me-2" />
                  Group Members ({selectedUsers.length})
                </Form.Label>
                <ListGroup className="user-list">
                  {selectedUsers.map((user) => (
                    <ListGroup.Item
                      key={user.id}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <FaUser className="me-2 text-primary" />
                        {user.label}
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveUser(user.id)}
                        disabled={saving}
                      >
                        <FaTrash />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
            </Row>
          )}

          {selectedUsers.length === 0 && (
            <Alert variant="info" className="mb-0">
              <small>
                <strong>No users added yet.</strong> Use the search above to add users to this group.
              </small>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel} disabled={saving}>
            <FaTimes className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                {editingGroup ? 'Update Group' : 'Create Group'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserGroupForm;
